import { useEffect, useId, useRef, type ReactNode } from 'react';
import { X, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import { cx } from '../../lib/cx';
import { Icon } from '../Icon';
import { IconButton } from './icon-button';

export interface DialogProps {
  /** `wide` is for form dialogs such as "Add staff"; `default` for short confirmations. */
  size?: 'default' | 'wide';
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** Staff confirmation dialogs (Figma A16): a rounded icon tile above the title, and roomier padding. */
  icon?: PhosphorIcon;
  /**
   * `form` is the tighter layout of the staff dialogs (Figma A04.1 "Add staff", "Suspend access"): 22/24/20 padding,
   * 16px between sections, a 20px radius and a quieter heading block.
   */
  density?: 'default' | 'form' | 'confirm';
  /**
   * `confirm` is Figma P01's confirm and session dialogs: 24 all round, 12 between everything, a 20 radius, and no
   * close cross (the buttons are the way out).
   */
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
}

/**
 * Hook shared by Dialog and Drawer. The native <dialog> gives us a real modal for free: focus is
 * trapped and restored, Esc closes, the page behind is inert, and it is announced as a dialog.
 */
export function useModalDialog(open: boolean, onClose: () => void) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return {
    ref,
    dialogProps: {
      onClose,
      // A click that lands on the <dialog> itself (not its content) is a click on the backdrop.
      onClick: (e: React.MouseEvent<HTMLDialogElement>) => {
        if (e.target === e.currentTarget) onClose();
      },
    },
  };
}

function Header({
  id,
  title,
  description,
  icon,
  form = false,
  confirm = false,
  onClose,
}: {
  id: string;
  title: string;
  description?: string;
  icon?: PhosphorIcon;
  form?: boolean;
  confirm?: boolean;
  onClose: () => void;
}) {
  const heading = (
    <div
      className={cx(
        'flex min-w-0 flex-col',
        form ? 'gap-(--mbm-space-1)' : confirm ? 'gap-(--mbm-space-3)' : 'gap-(--mbm-space-2)',
      )}
    >
      <h2 id={id} className={cx('text-title-sheet', form ? 'text-text-strong' : 'text-text-primary')}>
        {title}
      </h2>
      {description ? (
        <p
          className={cx(
            'text-text-secondary',
            form ? 'text-body-small' : confirm ? 'text-body-compact' : 'text-body-medium',
          )}
        >
          {description}
        </p>
      ) : null}
    </div>
  );

  if (confirm) return <header>{heading}</header>;

  if (icon) {
    return (
      <header className="flex flex-col gap-(--mbm-space-4)">
        <div className="flex items-start justify-between">
          <span className="flex size-(--mbm-size-button-compact) items-center justify-center rounded-(--mbm-radius-md) bg-surface-tint text-text-primary">
            <Icon icon={icon} size="lg" />
          </span>
          <IconButton icon={X} variant="ghost" aria-label="Close" onClick={onClose} />
        </div>
        {heading}
      </header>
    );
  }

  return (
    <header className="flex items-start justify-between gap-(--mbm-space-4)">
      {heading}
      <IconButton
        icon={X}
        variant="ghost"
        aria-label="Close"
        onClick={onClose}
        className={form ? '-mr-(--mbm-space-2-5) -mt-(--mbm-space-2)' : undefined}
      />
    </header>
  );
}

/** Short, centred confirmation or form. Use Drawer for create/edit flows (KT §5.8). */
export function Dialog({
  open,
  onClose,
  title,
  description,
  icon,
  children,
  footer,
  className,
  size = 'default',
  density = 'default',
}: DialogProps) {
  const titleId = useId();
  const { ref, dialogProps } = useModalDialog(open, onClose);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      className={cx(
        'm-auto bg-surface-elevated p-0 text-text-primary shadow-(--mbm-shadow-lg) backdrop:bg-overlay-scrim',
        size === 'wide'
          ? 'w-[min(var(--mbm-layout-dialog-wide-width),calc(100vw-var(--mbm-space-8)))]'
          : 'w-[min(var(--mbm-layout-dialog-width),calc(100vw-var(--mbm-space-8)))]',
        density === 'default' ? 'rounded-(--mbm-radius-lg)' : 'rounded-(--mbm-component-radius-card)',
        className,
      )}
      {...dialogProps}
    >
      {open ? (
        <div
          className={cx(
            'flex flex-col',
            density === 'form'
              ? 'gap-(--mbm-space-4) px-(--mbm-space-6) pb-(--mbm-space-5) pt-(--mbm-space-5-5)'
              : density === 'confirm'
                ? 'gap-(--mbm-space-3) p-(--mbm-space-6)'
                : cx('gap-(--mbm-space-5)', icon ? 'p-(--mbm-space-7)' : 'p-(--mbm-space-6)'),
          )}
        >
          <Header
            id={titleId}
            title={title}
            description={description}
            icon={icon}
            form={density === 'form'}
            confirm={density === 'confirm'}
            onClose={onClose}
          />
          {children}
          {footer ? (
            <footer
              className={cx(
                'flex flex-col-reverse sm:flex-row sm:justify-end',
                density === 'confirm' ? 'gap-(--mbm-space-2-5)' : 'gap-(--mbm-space-2)',
              )}
            >
              {footer}
            </footer>
          ) : null}
        </div>
      ) : null}
    </dialog>
  );
}

export interface DrawerProps extends Omit<DialogProps, 'size'> {
  /** Which edge the sheet slides from. Create/edit flows use the default `right`; the mobile nav uses `left`. */
  side?: 'right' | 'left';
  /** `form` is the narrower, roomier create-form sheet (Figma A05.1b "New project"); `wide` the 560 one of "Add to vault" (A05.11b). */
  size?: 'default' | 'form' | 'wide';
}

/** Right-hand sheet for create/edit flows (new project, add staff, approve meeting). */
export function Drawer({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  className,
  side = 'right',
  size = 'default',
}: DrawerProps) {
  const titleId = useId();
  const { ref, dialogProps } = useModalDialog(open, onClose);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      className={cx(
        'my-0 h-dvh max-h-none max-w-none bg-surface-elevated p-0 text-text-primary shadow-(--mbm-shadow-lg) backdrop:bg-overlay-scrim',
        size === 'form'
          ? 'w-[min(var(--mbm-layout-form-drawer-width),100vw)]'
          : size === 'wide'
            ? 'w-[min(var(--mbm-layout-vault-drawer-width),100vw)]'
            : 'w-[min(var(--mbm-layout-drawer-width),100vw)]',
        side === 'right' ? 'ml-auto mr-0 rounded-l-(--mbm-radius-lg)' : 'ml-0 mr-auto rounded-r-(--mbm-radius-lg)',
        className,
      )}
      {...dialogProps}
    >
      {open ? (
        <div
          className={cx(
            'flex h-full flex-col gap-(--mbm-space-5)',
            size === 'default' ? 'p-(--mbm-space-6)' : 'p-(--mbm-layout-form-drawer-padding)',
          )}
        >
          <Header id={titleId} title={title} description={description} onClose={onClose} />
          <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
          {footer ? (
            <footer className="flex flex-col-reverse gap-(--mbm-space-2) sm:flex-row sm:justify-end">{footer}</footer>
          ) : null}
        </div>
      ) : null}
    </dialog>
  );
}
